"use strict";

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Tooltip } from "@mui/material";
import { GET_DIALOGUE_ATTENTION_COUNTS } from "../../ApiEndpoints";

const numberFormat = new Intl.NumberFormat();
// Lets a burst of line edits settle before counting the project again.
const REFRESH_DELAY_MS = 1500;

// Labels match the filters dropdown; `filters` is what clicking one applies.
// Right length has no filter, so it isn't clickable.
const OUTCOMES = [
  {
    key: "translationRightLength",
    label: "Right length",
    color: "#1976D2",
  },
  {
    key: "translationLonger",
    label: "Make It Longer",
    color: "rgba(0, 0, 0, 0.35)",
    filters: { attention: "translation-longer" },
  },
  {
    key: "translationShorter",
    label: "Make It Shorter",
    color: "#E0A23B",
    filters: { attention: "translation-shorter" },
  },
  {
    key: "noGeneratedAudio",
    label: "No generated audio",
    color: "error.main",
    filters: { hasTranslatedAudio: "false" },
  },
];

// Project-wide counts of lines needing work, styled like the admin actions
// progress bar. `refreshKey` changes whenever the lines may have changed.
const DialogueAttentionProgress = ({
  projectId,
  languageCode,
  refreshKey,
  onSelect,
  disabled = false,
}) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!projectId || !languageCode) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(GET_DIALOGUE_ATTENTION_COUNTS, {
          projectId,
          translationLanguageCode: languageCode,
        });
        if (!cancelled) {
          setCounts(response.data);
        }
      } catch (err) {
        console.error(err);
      }
    }, counts ? REFRESH_DELAY_MS : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `counts` only picks the delay, so it isn't a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, languageCode, refreshKey]);

  if (!counts) {
    return null;
  }

  const total = counts.total || 0;
  const items = OUTCOMES.map((item) => ({
    ...item,
    count: counts[item.key] || 0,
  }));
  const share = (count) => (total > 0 ? (count / total) * 100 : 0);
  const summary = items
    .map((item) => `${numberFormat.format(item.count)} ${item.label}`)
    .join(", ");
  let segmentsBefore = 0;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Box
        role="img"
        aria-label={`${summary} of ${numberFormat.format(total)} lines`}
        sx={{
          display: "flex",
          height: 6,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: "rgba(0, 0, 0, 0.08)",
        }}
      >
        {items.map((item) => {
          const separated = segmentsBefore > 0;
          segmentsBefore += item.count;
          const width = share(item.count);
          return (
            <Box
              key={item.key}
              sx={{
                width: `${width}%`,
                bgcolor: item.color,
                // A hairline keeps adjacent segments distinct.
                borderLeft: separated && width > 0 ? "2px solid white" : "none",
                transition: "width 400ms ease",
              }}
            />
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: 1.5,
          rowGap: 0.25,
          fontSize: 12,
          color: "text.secondary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {items.map((item) => (
          <Tooltip
            key={item.key}
            title={item.filters ? `Show ${item.label.toLowerCase()} lines` : ""}
            placement="bottom"
            arrow
          >
            <Box
              {...(item.filters
                ? {
                    component: "button",
                    type: "button",
                    disabled,
                    onClick: () => onSelect?.(item.filters),
                  }
                : { component: "span", "data-static": true })}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                p: 0,
                border: 0,
                background: "none",
                font: "inherit",
                color: "inherit",
                cursor: "pointer",
                borderRadius: "4px",
                "&:hover:not(:disabled) .legend-label": {
                  textDecoration: "underline",
                },
                "&:disabled, &[data-static]": { cursor: "default" },
                "&[data-static] .legend-label": { textDecoration: "none" },
                "&:focus-visible": {
                  outline: "2px solid var(--color-link-text)",
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                component="span"
                aria-hidden
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: item.count > 0 ? item.color : "rgba(0, 0, 0, 0.2)",
                }}
              />
              <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                {numberFormat.format(item.count)}
              </Box>
              <span className="legend-label">{item.label}</span>
            </Box>
          </Tooltip>
        ))}
        <Box component="span" sx={{ ml: "auto" }}>
          {`of ${numberFormat.format(total)} lines`}
        </Box>
      </Box>
    </Box>
  );
};

export default DialogueAttentionProgress;
